import { useEffect, useState, useCallback } from "react";
import { Loader2, RefreshCw, CheckCircle2, Mail, Download, Gift } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ferr, Badge, money, date, Modal, downloadPdf, SearchBar, Table } from "../shared";

function CompModal({ onClose, onDone }) {
  const [f, setF] = useState({ email: "", full: "", lat: "", lng: "", send_email: true });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/admin/reports/comp", {
        email: f.email,
        address: { full: f.full, lat: f.lat ? Number(f.lat) : null, lng: f.lng ? Number(f.lng) : null },
        send_email: f.send_email,
      });
      toast.success("Complimentary report created."); onDone();
    } catch (e) { ferr(e, "Could not create report."); } finally { setBusy(false); }
  };
  return (
    <Modal title="Comp a report" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="admin-comp-form">
        <p className="text-[13px] text-mortar">Create a free, completed report on an existing customer's account.</p>
        <div><label className="label-mtr">Customer email</label><input className="input-mtr" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="admin-comp-email" /></div>
        <div><label className="label-mtr">Property address</label><input className="input-mtr" required value={f.full} onChange={(e) => setF({ ...f, full: e.target.value })} placeholder="123 Main St, City, ST 00000" data-testid="admin-comp-address" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="label-mtr">Lat (optional)</label><input className="input-mtr" value={f.lat} onChange={(e) => setF({ ...f, lat: e.target.value })} /></div>
          <div><label className="label-mtr">Lng (optional)</label><input className="input-mtr" value={f.lng} onChange={(e) => setF({ ...f, lng: e.target.value })} /></div>
        </div>
        <label className="flex items-center gap-2 text-[13px] text-navy"><input type="checkbox" checked={f.send_email} onChange={(e) => setF({ ...f, send_email: e.target.checked })} data-testid="admin-comp-notify" /> Email the customer it's ready</label>
        <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="admin-comp-submit">{busy ? <Loader2 className="animate-spin" size={18} /> : "Create report"}</button>
      </form>
    </Modal>
  );
}

export default function ReportsPanel() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showComp, setShowComp] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/reports", { params: { q, status: status || undefined } }); setRows(data); }
    catch (e) { ferr(e, "Failed to load reports."); } finally { setLoading(false); }
  }, [q, status]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const act = async (id, kind) => {
    try {
      await api.post(`/admin/reports/${id}/${kind}`);
      toast.success(kind === "resend-email" ? "Email re-sent." : kind === "retry" ? "Re-queued." : "Marked complete.");
      load();
    } catch (e) { ferr(e, "Action failed."); }
  };

  return (
    <div data-testid="admin-reports">
      <div className="flex flex-wrap gap-2 items-center mb-4">
        <div className="flex-1 min-w-[200px]"><SearchBar value={q} onChange={setQ} placeholder="Search address, report id, user…" testid="admin-reports-search" /></div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="input-mtr !min-h-[44px] !w-auto" data-testid="admin-reports-status">
          <option value="">All statuses</option>
          {["complete", "processing", "in_queue", "failed", "pending_payment"].map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <button onClick={() => setShowComp(true)} className="btn-secondary !min-h-[44px]" data-testid="admin-comp-open"><Gift size={16} /> Comp report</button>
      </div>
      <Table cols={["Address", "Customer", "Status", "Price", "Created", ""]} loading={loading} empty={!rows.length}>
        {rows.map((r) => (
          <tr key={r.report_id} className="border-t border-gravel hover:bg-fieldstone" data-testid={`admin-report-row-${r.report_id}`}>
            <td className="td max-w-[220px] truncate">{r.address?.full || r.report_id}{r.comp && <span className="ml-1 text-[10px] font-bold text-violet-600">COMP</span>}</td>
            <td className="td">{r.user_email || "—"}</td>
            <td className="td"><Badge value={r.status} /></td>
            <td className="td">{money(r.customer_price)}</td>
            <td className="td whitespace-nowrap text-[12px]">{date(r.created_at)}</td>
            <td className="td text-right whitespace-nowrap">
              <button onClick={() => act(r.report_id, "retry")} className="icon-btn" title="Re-queue" data-testid={`admin-report-retry-${r.report_id}`}><RefreshCw size={15} /></button>
              {r.status !== "complete" && <button onClick={() => act(r.report_id, "complete")} className="icon-btn" title="Force complete" data-testid={`admin-report-complete-${r.report_id}`}><CheckCircle2 size={15} /></button>}
              {r.status === "complete" && <>
                <button onClick={() => act(r.report_id, "resend-email")} className="icon-btn" title="Resend email" data-testid={`admin-report-email-${r.report_id}`}><Mail size={15} /></button>
                <button onClick={() => downloadPdf(r.report_id)} className="icon-btn" title="Download PDF" data-testid={`admin-report-pdf-${r.report_id}`}><Download size={15} /></button>
              </>}
            </td>
          </tr>
        ))}
      </Table>
      {showComp && <CompModal onClose={() => setShowComp(false)} onDone={() => { setShowComp(false); load(); }} />}
    </div>
  );
}
